import { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { parsePackageCodeFromScan } from '../utils/packageTrack';

export default function usePackageCameraScan({ onDetect, debounceMs = 2500, active = false }) {
  const [cameraError, setCameraError] = useState('');
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const lastDetectedRef = useRef({ code: '', at: 0 });
  const onDetectRef = useRef(onDetect);

  useEffect(() => {
    onDetectRef.current = onDetect;
  }, [onDetect]);

  useEffect(() => {
    if (!active || !videoRef.current) return undefined;

    const videoEl = videoRef.current;
    const reader = new BrowserMultiFormatReader();
    setCameraError('');
    let cancelled = false;

    reader
      .decodeFromVideoDevice(undefined, videoEl, (result) => {
        if (cancelled || !result) return;
        const parsed = parsePackageCodeFromScan(result.getText());
        if (!parsed) return;
        const now = Date.now();
        if (
          parsed === lastDetectedRef.current.code
          && now - lastDetectedRef.current.at < debounceMs
        ) {
          return;
        }
        lastDetectedRef.current = { code: parsed, at: now };
        onDetectRef.current?.(parsed);
      })
      .then((controls) => {
        if (cancelled) {
          controls.stop();
          return;
        }
        controlsRef.current = controls;
      })
      .catch((e) => {
        setCameraError(e.message || 'Camera access denied. Allow camera permission and try again.');
      });

    return () => {
      cancelled = true;
      if (controlsRef.current) {
        try {
          controlsRef.current.stop();
        } catch {
          /* already stopped */
        }
        controlsRef.current = null;
      }
      if (videoEl) {
        BrowserMultiFormatReader.cleanVideoSource(videoEl);
      }
      BrowserMultiFormatReader.releaseAllStreams();
    };
  }, [active, debounceMs]);

  return { videoRef, cameraError, setCameraError };
}
